import { ExpandLess, ExpandMore, PersonPinRounded } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Typography, useMediaQuery } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { FC } from 'react';

import ReceivedSessionShortStats from '@components/ReceivedSessionShortStats';
import { useToogle } from '@hooks/useToogle';
import { BarcodeIcon } from 'src/assets/icons/BarcodeIcon';
import { ReviewItemsFilterType } from 'src/types/shared';

import PendingLine from './PendingLine';
import StatLine from './StatLine';
import { containerSx, headerContainerSx, titleContaineSx } from './styles';

type Props = {
  processing?: number;
  requireValidation: number;
  unassigned: number;
  missingTracking: number;
  duplicate: number;
  total?: number;
  onFilterClick: (type: ReviewItemsFilterType | null) => void;
};

const ReviewStats: FC<Props> = ({
  processing,
  requireValidation,
  unassigned,
  missingTracking,
  duplicate,
  total,
  onFilterClick,
}) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'));
  const { value, toggle } = useToogle(!isMobile);
  const Icon = value ? ExpandLess : ExpandMore;

  const anyCategoryItem = !!requireValidation || !!unassigned || !!missingTracking || !!duplicate;

  return (
    <>
      <Box sx={containerSx}>
        <Box sx={headerContainerSx}>
          <Box sx={titleContaineSx}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Summary
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6">{total} items</Typography>
            </Box>
          </Box>
          {anyCategoryItem && (
            <IconButton onClick={toggle} sx={{ width: 30, height: 30 }}>
              <Icon />
            </IconButton>
          )}
        </Box>
        {!value && anyCategoryItem && (
          <ReceivedSessionShortStats
            processing={processing}
            requireValidation={requireValidation}
            missingTracking={missingTracking}
            unassigned={unassigned}
            duplicate={duplicate}
            isDark
            onClick={onFilterClick}
          />
        )}
        {value && anyCategoryItem && (
          <>
            {!!processing && <PendingLine count={processing} />}
            {!!requireValidation && (
              <StatLine
                onClick={() => onFilterClick(ReviewItemsFilterType.REQUIRE_VALIDATION)}
                Icon={PersonPinRounded}
                count={requireValidation}
                text={ReviewItemsFilterType.REQUIRE_VALIDATION}
                color="warning"
              />
            )}
            {!!missingTracking && (
              <StatLine
                onClick={() => onFilterClick(ReviewItemsFilterType.MISSING_TRACKING_NUMBER)}
                Icon={BarcodeIcon}
                count={missingTracking}
                text={ReviewItemsFilterType.MISSING_TRACKING_NUMBER}
                color="warning"
              />
            )}
            {!!unassigned && (
              <StatLine
                onClick={() => onFilterClick(ReviewItemsFilterType.UNASSIGNED)}
                Icon={PersonPinRounded}
                count={unassigned}
                text={ReviewItemsFilterType.UNASSIGNED}
                color="error"
              />
            )}
            {!!duplicate && (
              <StatLine
                onClick={() => onFilterClick(ReviewItemsFilterType.DUPLICATE)}
                Icon={BarcodeIcon}
                count={duplicate}
                text={ReviewItemsFilterType.DUPLICATE}
                color="error"
              />
            )}
            <Button variant="soft" size="small" sx={{ py: '6px', mt: '4px' }} onClick={() => onFilterClick(ReviewItemsFilterType.ALL)}>
              View All Issues
            </Button>
          </>
        )}
      </Box>
      <Divider sx={{ borderStyle: 'dashed' }} />
    </>
  );
};

export default ReviewStats;
